import { i18n } from "$/i18n"
import { Option } from "$/models/option"

import { EPath, ERoute } from "@/3514/constants"
import { EUserRole } from "@/3514/types"

export const ROOT_ELEMENT_SELECTOR: string = "main-app"
export const JS_CONF = JSAPP_CONF

export const INDEX_URL = "/"

export enum Role {
    Participant = "participant",
    Coach = "coach",
    ProgramManager = "program_manager",
    Coachee = "coachee"
}

export enum ServerStateKeys {
    WaitingListText = "waitingListText",
    Surveys = "Participant_Surveys",
    Offboarding = "offboarding",
    Nps = "Nps",
    SubUsers = "subUsers"
}

export enum ReflectionComponentType {
    FREE_RESPONSE = "question",
    LIKERT_GROUP = "question_variants",
    COLORED_LIKERT_GROUP = "question_variants_color"
}

export enum Reflection {
    OBJECTIVE = "reflection_objective",
    FINAL = "reflection_final"
}

export const defaultAllowedStyles = {
    "*": {
        color: [/^#(0x)?[0-9a-f]+$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/],
        "text-align": [/^left$/, /^right$/, /^center$/],
        "font-size": [/^\d+(?:px|em|%)$/]
    }
}

/**
 * @deprecated new routes are placed in ERoute enum
 */
export enum ParticipantUrls {
    WAITING_LIST = "/waiting-list",
    SWAP_SESSION = "/swap-session/:sessionId",
    SURVEYS = "/surveys",
    SURVEY_BY_CODE = "/survey/:access_code?",
    PARTICIPANT_REDIRECT_URL_PAGE = "/post-journey-reflection",
    END_OF_JOURNEY_PAGE = "/end-journey",
    OFFBOARDING = "/offboarding",
    PROFILE = "/profile",
    COACHING_PLAN = "/coaching-plan",
    COACH_RATING = "/session-feedback/:sessionId",
    WAITING_LIST_SSO = "/waiting-list/unrecognized-sso/:orgSlug",
    COACHEES = "/simulab-coachees",
    SIMULAB_CHAT = "/simulab/:conversationId",
    ASSIGN_MODULE = "/assign-module",
    /**
     * @deprecated kept for backward compatibility; relevant is ERoute.Participant_CoachingSessions
     */
    SESSION_LIST = "/session-list"
}

/**
 * @deprecated new routes are placed in ERoute enum
 */
export enum CoachUrls {
    WELCOME = "/welcome",
    ITINERARY = "/itinerary",
    PARTICIPANT_DETAILS = "/participant/:id",
    CALENDAR_AVAILABILITY = "/calendar-settings/availability/",
    CALENDAR_LIST = "/calendar/list/",
    INDIVIDUAL_SESSIONS = "/session-participant/:id",
    PARTICIPANT_COACHING_PLAN = "/coaching-plan-participant/:participantId"
}

export const MAX_WIDTH_MOBILE = 767
export const MAX_WIDTH_SMALL_MOBILE = 576

export const MAX_WIDTH_MOBILE_MEDIA = `(max-width: ${MAX_WIDTH_MOBILE}px)`
export const MAX_WIDTH_SMALL_MOBILE_MEDIA = `(max-width: ${MAX_WIDTH_SMALL_MOBILE}px)`

export const DATE_FORMAT = {
    SHORT_MONTH_DAY_NOT_PADDED: "LLL d",
    WEEKDAYDD: "EEE dd",
    SHORT_WEEK_SHORT_MONTH_DAY: "EEE, MMM d",
    HOUR_MINUTES_MERIDIEM: "h:mm a",
    HOUR_MINUTES_MERIDIEM_SPACELESS: "h:mma",
    TIMEZONE_ABBREVIATED: "ZZZZ",
    MONTH_DAY_YEAR: "LLL. d, yyyy",
    SHORT_MONTH_ZERO_DAY: "LLL. dd",
    SHORT_DAY_SHORT_MONTH_NUMBER: "ccc, LLL d",
    SHORT_DAY_SHORT_MONTH: "MMM, d"
}

export const CARE_TEAM_EMAIL = "mailto:care@wisermanagers.com"

export const DEFAULT_TRANSITION_TIME = 300
export const MAX_JS_TIMEOUT_VALUE_IN_MS: number = 2147483647

/**
 * @deprecated used for barely supported Cronofy widgets
 */
export enum CronofyNotificationType {
    ERROR = "error",
    SLOT_SELECTED = "slot_selected",
    NO_SLOTS_FOUND = "no_slots_found"
}

export enum CoachRatingAskedFrom {
    POST_MODULE_REFLECTION_POPUP = "post-module",
    OFFBOARDING = "offboarding"
}

export type CoachRatingNumber = 1 | 2 | 3 | 4 | 5

export type CoachRatingSource = CoachRatingAskedFrom.POST_MODULE_REFLECTION_POPUP | CoachRatingAskedFrom.OFFBOARDING

export const ERROR_TEXT = i18n.t(
    "Uh oh! Something has gone wrong. Our team is looking into it. Please try again or contact support."
)

export const MAIN_PORTAL_ID = "modal-portal"
export const SECONDARY_PORTAL_ID = "secondary-portal"

export enum ProductType {
    SPOT_COACHING = "spot-coaching",
    LEADERSHIP = "leadership-coaching",
    EXECUTIVE = "executive-coaching",
    WISER_DEMO = "wiser-demo",
    WISER = "wiser"
}

export const DEFAULT_LIKERT_COLOR_VAR = "var(--likert-choice-radio-selected-color)"

export const COACHING_GOAL_STATUS = {
    All: "all",
    Active: "active",
    Archived: "archived"
} as const

export type CoachingGoalStatus = (typeof COACHING_GOAL_STATUS)[keyof typeof COACHING_GOAL_STATUS]

export const COACHING_PLAN_MENU_ITEMS = [
    new Option({ value: "edit", label: i18n.t("Edit") }),
    new Option({ value: "archive", label: i18n.t("Archive") })
]

export const COLORS = {
    BRAND_COLOR: "#fd4d00",
    BRAND_SECONDARY: "#6f6f6f",
    BRAND_DARK: "#000000",
    BRAND_BG: "#f3f3f3",
    BRAND_ACCENT: "#b0b0b0",
    BRAND_ACCENT_LIGHT: "#e3e3e3",
    BRAND_SECONDARY_LIGHT: "#FFC104"
}

export const ANIMATION_LINKS = {
    CONFETTI: "https://assets10.lottiefiles.com/packages/lf20_fJ7CVd.json",
    SPINNER: "https://lottie.host/039924fb-9a0d-4c7b-a1dd-208991e48611/s9bCTlrwxw.json"
}

export const WISER_COACHING_LINK = "http://www.wisercoaching.com"

export const COACHING_PLAN_ARCHIVE_ITEMS = [new Option({ value: "unarchive", label: i18n.t("Unarchive") })]

const coachRoutePaths: string[] = [
    ...Object.values(CoachUrls),
    ...[ERoute.Coach_Dashboard, "/group-session/:sessionId", "/setup-calendar/accounts", "/relink-accounts"]
]
const participantRoutePath: string[] = [
    ...Object.values(ParticipantUrls),
    ...[
        EPath.Participant_Dashboard(),
        `${EPath.Participant_Dashboard()}/:moduleId`,
        EPath.Participant_Chapter(),
        `${EPath.Participant_Chapter()}/:chapterId`,
        EPath.Participant_CoachingSessions(),
        EPath.Participant_OnboardingProfile(),
        EPath.Participant_OnboardingVideo(),
        EPath.Participant_OnboardingModules_Legacy(),
        EPath.Participant_OnboardingDiagnostic(),
        EPath.Participant_OnboardingDiagnosticResults_Legacy(),
        EPath.Participant_OnboardingReflection(),
        EPath.Participant_OnboardingHolds(),
        EPath.Participant_OnboardingCoach(),
        EPath.Participant_OnboardingRedirect()
    ]
]

export const userRoutesPath = {
    [EUserRole.Participant]: participantRoutePath,
    [EUserRole.Coach]: coachRoutePaths,
    [EUserRole.Manager]: coachRoutePaths
}
